import {
	VolunteersEventType,
	EventDetailLocalizableFormData,
	EventFormData,
	EventPreviewLocalizableFormData,
	EventTextRules,
	UpdateEventRequest,
} from "../context/EventContext/constants";
import { Culture, cultureLabels } from "../context/VolunteersHighlightContext/constants";
import { isValidUrl } from "../utils/validation";
import { countText } from "../utils/textLength";
import dayjs from "dayjs";

interface LengthCheck {
	value?: string;
	max: number;
	label: string;
	// Rich text: the limit covers the HTML, formatting included.
	isRichText?: boolean;
}

// The first text of one language that is longer than its limit, as a message naming the field. The backend trims a
// text before it counts it.
const findTooLongText = (culture: string, checks: LengthCheck[]): string | null => {
	const tooLong = checks.find(check => countText(check.value?.trim(), EventTextRules.lineBreaks) > check.max);
	if (!tooLong) {
		return null;
	}
	const language = cultureLabels[culture as Culture] ?? culture;
	return tooLong.isRichText
		? `${language} "${tooLong.label}" metni, biçimlendirmesiyle birlikte en fazla ${tooLong.max} karakter olmalıdır.`
		: `${language} ${tooLong.label} en fazla ${tooLong.max} karakter olmalıdır.`;
};

const getPreviewLengthChecks = (item: { header?: string; previewText?: string; categoryTag?: string }): LengthCheck[] => [
	{ value: item.header, max: EventTextRules.previewHeaderMaxLength, label: "önizleme başlığı" },
	{ value: item.previewText, max: EventTextRules.previewTextMaxLength, label: "önizleme metni" },
	{ value: item.categoryTag, max: EventTextRules.categoryTagMaxLength, label: "kategori etiketi" },
];

interface DetailTexts {
	header?: string;
	detailText?: string;
	processText?: string;
	applicantText?: string;
	durationText?: string;
	customButtonText?: string;
	customButtonUrl?: string;
}

// The duration text and the custom button exist only for one kind of project; the add form leaves the other kind's
// fields out, the update request carries only the fields that changed.
const getDetailLengthChecks = (item: DetailTexts, options: { duration: boolean; customButton: boolean }): LengthCheck[] => [
	{ value: item.header, max: EventTextRules.detailHeaderMaxLength, label: "detay başlığı" },
	{ value: item.detailText, max: EventTextRules.detailTextMaxLength, label: "Program Detayı", isRichText: true },
	{ value: item.processText, max: EventTextRules.detailTextMaxLength, label: "Program Süreci", isRichText: true },
	{ value: item.applicantText, max: EventTextRules.detailTextMaxLength, label: "Programa Kimler Başvurabilir?", isRichText: true },
	...(options.duration
		? [{ value: item.durationText, max: EventTextRules.durationTextMaxLength, label: "Başvurular Devam Ediyor", isRichText: true }]
		: []),
	...(options.customButton
		? [
			{ value: item.customButtonText, max: EventTextRules.customButtonTextMaxLength, label: "buton metni" },
			{ value: item.customButtonUrl, max: EventTextRules.customButtonUrlMaxLength, label: "buton URL'si" },
		]
		: []),
];

const TEXT_PATTERN = /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@]*$/u;

const isShortTextInvalid = (value: string | undefined, max: number) => !!value?.trim() && (value.trim().length > max || !TEXT_PATTERN.test(value.trim()));

const stripHtml = (value?: string) => (value ?? "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

// The first error of the items, checked in their order.
const findFirstError = <T,>(items: T[], validate: (item: T) => string | null): string | null => {
	for (const item of items) {
		const error = validate(item);
		if (error) {
			return error;
		}
	}
	return null;
};

// The project's own texts, its tags and its order: the same rules on add and update.
const validateEventFields = (fields: { locationText?: string; projectDateText?: string; eventTagIds?: string[]; displayOrder?: number }) => {
	if (isShortTextInvalid(fields.locationText, EventTextRules.locationTextMaxLength)) {
		return "Lokasyon en fazla 50 karakter olmalı ve geçersiz karakter içermemelidir.";
	}
	if (isShortTextInvalid(fields.projectDateText, EventTextRules.projectDateTextMaxLength)) {
		return "Proje tarihi metni en fazla 50 karakter olmalı ve geçersiz karakter içermemelidir.";
	}
	if ((fields.eventTagIds?.length ?? 0) > 3) {
		return "En fazla 3 etiket seçebilirsiniz.";
	}
	if (fields.displayOrder !== undefined && fields.displayOrder < 0) {
		return "Sıralama negatif olamaz.";
	}
	return null;
};

// A project with applications needs its application status (and no past date); one without needs the button choice.
const validateTypeSettings = (data: EventFormData, isApplicationRequired: boolean) => {
	if (!isApplicationRequired) {
		return data.eventDetail.hasCustomButton === undefined ? "Lütfen özel buton durumunu seçin." : null;
	}
	if (data.isAppliable === undefined) {
		return "Lütfen başvuru durumunu seçin.";
	}
	if (data.projectDate && dayjs(data.projectDate).isBefore(dayjs(), "day")) {
		return "Proje tarihi geçmiş bir tarih olamaz.";
	}
	return null;
};

const validateNewPreview = (localizable: EventPreviewLocalizableFormData) => {
	if (!localizable.culture || !localizable.header?.trim() || !localizable.previewText?.trim() || !localizable.image) {
		return "Lütfen önizleme başlık, metin ve görsel alanlarını doldurun.";
	}
	return findTooLongText(localizable.culture, getPreviewLengthChecks(localizable));
};

const validateNewDetail = (localizable: EventDetailLocalizableFormData, isApplicationRequired: boolean, requiresCustomButton: boolean) => {
	if (!localizable.culture || !localizable.header?.trim() || !stripHtml(localizable.detailText) || !localizable.image) {
		return "Lütfen detay başlık, detay metni ve görsel alanlarını doldurun.";
	}
	if (requiresCustomButton && (!localizable.customButtonText?.trim() || !localizable.customButtonUrl?.trim())) {
		return "Lütfen özel buton metni ve URL alanlarını doldurun.";
	}
	const lengthError = findTooLongText(
		localizable.culture,
		getDetailLengthChecks(localizable, { duration: isApplicationRequired, customButton: requiresCustomButton })
	);
	if (lengthError) {
		return lengthError;
	}
	return requiresCustomButton && !isValidUrl(localizable.customButtonUrl.trim()) ? "Lütfen geçerli bir buton URL'si girin." : null;
};

const isKnownEventType = (eventType: VolunteersEventType) =>
	eventType === VolunteersEventType.ApplicationRequired || eventType === VolunteersEventType.ApplicationNotRequired;

export class EventValidator {
	static validateUpdateEvent = (data: UpdateEventRequest): string | null => {
		if (data.eventType !== undefined && !isKnownEventType(data.eventType)) {
			return "Geçersiz etkinlik tipi.";
		}

		const { locationText, projectDateText, eventTagIds } = data;
		return validateEventFields({ locationText, projectDateText, eventTagIds, displayOrder: data.eventPreview?.displayOrder })
			// Only the texts that changed are in the request.
			?? findFirstError(data.eventPreview?.eventPreviewLocalizable ?? [], item => findTooLongText(item.culture, getPreviewLengthChecks(item)))
			?? findFirstError(
				data.eventDetail?.eventDetailLocalizable ?? [],
				item => findTooLongText(item.culture, getDetailLengthChecks(item, { duration: true, customButton: true }))
			);
	};

	static validateAddEvent = (data: EventFormData): string | null => {
		if (data.eventType === undefined || data.eventType === null) {
			return "Lütfen etkinlik tipini seçin.";
		}

		const isApplicationRequired = data.eventType === VolunteersEventType.ApplicationRequired;
		const requiresCustomButton = !isApplicationRequired && !!data.eventDetail.hasCustomButton;

		const { locationText, projectDateText, eventTagIds } = data;
		return validateEventFields({ locationText, projectDateText, eventTagIds, displayOrder: data.eventPreview.displayOrder })
			?? validateTypeSettings(data, isApplicationRequired)
			?? findFirstError(data.eventPreview.eventPreviewLocalizable, validateNewPreview)
			?? findFirstError(data.eventDetail.eventDetailLocalizable, item => validateNewDetail(item, isApplicationRequired, requiresCustomButton));
	};
}
